import { CheckCircle2, CircleDashed } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-overline text-zinc-500">{label}</Label>
      {children}
    </div>
  );
}

export function SmallInput({ value, onChange, placeholder, className, testId, type }) {
  const isTime = type === "time";
  return (
    <Input
      data-testid={testId}
      type={type || "text"}
      value={isTime ? normalizeTimeForInput(value) : value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`bg-zinc-900 border-zinc-800 focus-visible:ring-amber-500 h-8 text-xs ${className || ""}`}
    />
  );
}

export function normalizeTimeForInput(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (/^\d{1,2}:\d{2}$/.test(s)) return s.length === 4 ? `0${s}` : s;
  const m = s.match(/^(\d{1,2}):(\d{2})\s?(am|pm)$/i);
  if (m) {
    let h = parseInt(m[1], 10);
    const mm = m[2];
    const ampm = m[3].toLowerCase();
    if (ampm === "pm" && h < 12) h += 12;
    if (ampm === "am" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${mm}`;
  }
  return "";
}

export function fmtTime(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (/^\d{1,2}:\d{2}\s?(am|pm)$/i.test(s)) return s.toLowerCase().replace(/\s/g, "");
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) {
    const h = parseInt(m[1], 10);
    const mm = m[2];
    const ampm = h < 12 ? "am" : "pm";
    const h12 = h % 12 || 12;
    return `${h12}:${mm}${ampm}`;
  }
  return s;
}

export function KV({ k, v, amber }) {
  return (
    <div>
      <div className="text-[8px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
        {k}
      </div>
      <div className={`text-[11px] ${amber ? "text-amber-700 font-semibold" : "text-zinc-900"}`}>
        {v || "-"}
      </div>
    </div>
  );
}

export function SectionH({ children }) {
  return (
    <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-zinc-900 border-b border-zinc-900 pb-0.5 mb-2 mt-3">
      {children}
    </div>
  );
}

function Placeholder() {
  return <span className="inline-block w-4 h-4 shrink-0" />;
}

export function ConfirmIcon({ email, teamId, confirmations }) {
  if (!confirmations || confirmations.length === 0) return <Placeholder />;
  const norm = (s) => (s || "").trim().toLowerCase();
  const match = confirmations.find(
    (c) =>
      (teamId && c.team_id === teamId) ||
      (email && norm(c.email) === norm(email))
  );
  if (!match) return <Placeholder />;
  if (match.confirmed_at) {
    return (
      <span
        title={`Confirmed ${new Date(match.confirmed_at).toLocaleString()}`}
        data-testid="confirm-check"
        className="inline-flex items-center justify-center w-4 h-4 shrink-0"
      >
        <CheckCircle2 className="w-4 h-4 text-emerald-400" strokeWidth={2.5} />
      </span>
    );
  }
  return (
    <span
      title={`Sent ${new Date(match.sent_at).toLocaleString()} · awaiting confirmation`}
      data-testid="confirm-pending"
      className="inline-flex items-center justify-center w-4 h-4 shrink-0"
    >
      <CircleDashed className="w-4 h-4 text-amber-500/70" />
    </span>
  );
}

export function groupCrewByDept(crewList) {
  const order = [
    "PRODUCTION", "CAMERA", "LIGHTING", "GRIP", "SOUND",
    "ART", "WARDROBE", "HAIR & MAKEUP", "POST", "OTHER",
  ];
  const map = {};
  crewList.forEach((c) => {
    const d = c.person.department || "OTHER";
    if (!map[d]) map[d] = [];
    map[d].push(c);
  });
  const result = [];
  order.forEach((d) => {
    if (map[d]) result.push([d, map[d]]);
  });
  Object.keys(map).forEach((d) => {
    if (!order.includes(d)) result.push([d, map[d]]);
  });
  return result;
}
